import React from 'react'
import { DateRange } from 'Components/DateRange'
import { DataGrid } from '@mui/x-data-grid'
import {
  Box,
  Grid,
  Button,
  TextField,
  MenuItem,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Stack,
  Chip,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab,
} from '@mui/material'
import { getRequest, deleteRequest, putRequest, postRequest } from 'Includes/api/apicall'
import api from 'Includes/api/api'
import { GET_URL, DEL_URL, PUT_URL } from 'Includes/urls'
import { Link, useLocation } from 'react-router-dom'
import { AddCircleOutlineOutlined, PlayArrow, CheckCircleOutline } from '@mui/icons-material'
import { X, Camera, Trash2, CheckCircle } from 'lucide-react'
import Swal from 'sweetalert2'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { DEFAULT_SCHOOL_REVIEW_TEMPLATE, filterFilledReviewSections, initialReviewCheckedConcepts, isConceptChecked, normalizeConceptsList, stripHtml, toggleReviewConcept } from './visitConcepts'
import CompanyChecklistTab from '../Company/CompanyChecklistTab'
import VisitSummaryTab from './VisitSummaryTab'

const STATUS_LABEL = {
  pending: 'Pending',
  visited: 'Visited',
  postponed: 'Postponed',
  cancel: 'Canceled',
}

function visitTimeForApi(t) {
  if (!t) return null
  if (typeof t === 'string' && t.length === 5) return `${t}:00`
  return t
}

function visitPayloadFromRow(row, overrides = {}) {
  return {
    company: row.company,
    description: row.description,
    visit_date: row.visit_date,
    visit_time:
      overrides.visit_time !== undefined ? overrides.visit_time : visitTimeForApi(row.visit_time),
    priority: row.priority,
    status: overrides.status !== undefined ? overrides.status : row.status,
    assigned_to:
      overrides.assigned_to !== undefined ? overrides.assigned_to : row.assigned_to ?? null,
    school_review:
      overrides.school_review !== undefined ? overrides.school_review : row.school_review ?? '',
    concepts_to_explain:
      overrides.concepts_to_explain !== undefined
        ? overrides.concepts_to_explain
        : normalizeConceptsList(row.concepts_to_explain),
    concepts_explained:
      overrides.concepts_explained !== undefined
        ? overrides.concepts_explained
        : normalizeConceptsList(row.concepts_explained),
    postponed_date:
      overrides.postponed_date !== undefined ? overrides.postponed_date : row.postponed_date ?? null,
    postponed_time:
      overrides.postponed_time !== undefined
        ? overrides.postponed_time
        : visitTimeForApi(row.postponed_time),
  }
}

const TRUNCATE_LEN = 40

function ExpandableCell({ value, isHtml = false }) {
  const [expanded, setExpanded] = React.useState(false)

  const displayValue = isHtml ? filterFilledReviewSections(value) : String(value || '').trim()
  if (!displayValue) return '-'

  const plainText = isHtml ? stripHtml(displayValue) : displayValue
  const needsMore = plainText.length > TRUNCATE_LEN

  const handleToggle = (e) => {
    e.stopPropagation()
    setExpanded((prev) => !prev)
  }

  const htmlContentSx = {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: 'text.primary',
    '& strong': { fontWeight: 700 },
    '& b': { fontWeight: 700 },
    '& p': { margin: '0 0 6px' },
  }

  if (!needsMore) {
    if (isHtml) {
      return (
        <Box sx={{ py: 0.5, width: '100%', ...htmlContentSx }} dangerouslySetInnerHTML={{ __html: displayValue }} />
      )
    }
    return (
      <Typography variant="body2" sx={{ py: 0.5, whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
        {displayValue}
      </Typography>
    )
  }

  return (
    <Box sx={{ py: 0.5, width: '100%' }}>
      {expanded ? (
        isHtml ? (
          <Box sx={htmlContentSx} dangerouslySetInnerHTML={{ __html: displayValue }} />
        ) : (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
            {displayValue}
          </Typography>
        )
      ) : (
        <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
          {`${plainText.slice(0, TRUNCATE_LEN)}...`}
        </Typography>
      )}
      <Button
        size="small"
        sx={{ p: 0, minWidth: 'auto', textTransform: 'none', mt: 0.25, fontWeight: 600 }}
        onClick={handleToggle}
      >
        {expanded ? 'Show less' : 'Show more'}
      </Button>
    </Box>
  )
}

function ConceptsExplainedCell({ value }) {
  const [expanded, setExpanded] = React.useState(false)
  const concepts = normalizeConceptsList(value)
  if (!concepts.length) return '-'

  const visible = expanded ? concepts : concepts.slice(0, 2)
  const needsMore = concepts.length > 2

  return (
    <Box sx={{ py: 0.5, width: '100%' }}>
      <Stack direction="row" flexWrap="wrap" gap={0.5}>
        {visible.map((concept) => (
          <Chip key={concept} label={concept} size="small" variant="outlined" color="primary" />
        ))}
      </Stack>
      {needsMore ? (
        <Button
          size="small"
          sx={{ p: 0, minWidth: 'auto', textTransform: 'none', mt: 0.25, fontWeight: 600 }}
          onClick={(e) => {
            e.stopPropagation()
            setExpanded((prev) => !prev)
          }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </Button>
      ) : null}
    </Box>
  )
}

function VisitList(props) {
  const location = useLocation()
  const myVisitsOnly = location.pathname === '/visits/my'

  const [rows, setRows] = React.useState([])
  const [rowCount, setRowCount] = React.useState(0)
  const [loading, setLoading] = React.useState(false)
  const [page, setPage] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(10)
  const [sortModel, setSortModel] = React.useState([{ field: 'visit_date', sort: 'asc' }])
  const [statusFilter, setStatusFilter] = React.useState('')
  const [searchTerm, setSearchTerm] = React.useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState('')
  const [summaryStats, setSummaryStats] = React.useState({ total: null, pending: null, visited: null, postponed: null, cancel: null })
  const [activeTab, setActiveTab] = React.useState(0)

  // Camera states
  const [showCamera, setShowCamera] = React.useState(false)
  const [capturedPhoto, setCapturedPhoto] = React.useState(null)
  const capturingVisitRef = React.useRef(null)
  const videoRef = React.useRef(null)
  const streamRef = React.useRef(null)
  const locationRef = React.useRef(null)

  const defaultStartDate = new Date();
  defaultStartDate.setDate(defaultStartDate.getDate() - 15);
  const defaultEndDate = new Date();
  defaultEndDate.setDate(defaultEndDate.getDate() + 15);

  const formatDateForInput = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const [startDate, setStartDate] = React.useState(formatDateForInput(defaultStartDate));
  const [endDate, setEndDate] = React.useState(formatDateForInput(defaultEndDate));

  const [users, setUsers] = React.useState([])
  const [assignOpen, setAssignOpen] = React.useState(false)
  const [assignRow, setAssignRow] = React.useState(null)
  const [assignUser, setAssignUser] = React.useState(null)
  const [assignSaving, setAssignSaving] = React.useState(false)

  const [statusOpen, setStatusOpen] = React.useState(false)
  const [statusRow, setStatusRow] = React.useState(null)
  const [dialogStatus, setDialogStatus] = React.useState('pending')
  const [dialogSchoolReview, setDialogSchoolReview] = React.useState('')
  const [dialogCheckedConcepts, setDialogCheckedConcepts] = React.useState([])
  const [dialogPostponedDate, setDialogPostponedDate] = React.useState('')
  const [dialogPostponedTime, setDialogPostponedTime] = React.useState('')
  const [statusSaving, setStatusSaving] = React.useState(false)

  const userInfo = React.useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), [])

  const allowedCodes = React.useMemo(() => {
    if (Array.isArray(userInfo?.permission_codenames) && userInfo.permission_codenames.length) {
      return new Set(userInfo.permission_codenames.filter(Boolean))
    }
    const ownCodes = (userInfo?.user_permissions || userInfo?.permissions || []).map((p) =>
      typeof p === 'string' ? p : p?.codename
    )
    const groupCodes = (userInfo?.groups || [])
      .flatMap((g) => g?.permissions || [])
      .map((p) => (typeof p === 'string' ? p : p?.codename))
    return new Set([...ownCodes, ...groupCodes].filter(Boolean))
  }, [userInfo])
  const isSuperuser = Boolean(userInfo?.is_superuser)
  const canAdd = isSuperuser || allowedCodes.has('visible_company_visit_add')
  const canChange = isSuperuser || allowedCodes.has('visible_company_visit_change')
  const canDelete = isSuperuser || allowedCodes.has('visible_company_visit_delete')

  const canChangeStatus = myVisitsOnly || canChange
  const showAssign = !myVisitsOnly && canChange

  const loadUsers = React.useCallback(() => {
    getRequest(GET_URL.user.api, { limit: 300, pageno: 1, status: 'active' }, props).then((response) => {
      const data = response?.data?.data || []
      setUsers(Array.isArray(data) ? data : [])
    })
  }, [props])

  React.useEffect(() => {
    loadUsers()
  }, [loadUsers])

  React.useEffect(() => {
    if (!assignOpen || !assignRow) return
    const uid = assignRow.assigned_to
    const u = uid != null ? users.find((x) => String(x.id) === String(uid)) : null
    setAssignUser(u || null)
  }, [assignOpen, assignRow, users])

  const formatTime = (t) => {
    if (!t) return '-'
    if (typeof t === 'string' && t.length >= 5) return t.slice(0, 5)
    return String(t)
  }

  const openAssign = (row) => {
    if (!row?.id) return
    setAssignRow(row)
    const uid = row.assigned_to
    const u = uid != null ? users.find((x) => String(x.id) === String(uid)) : null
    setAssignUser(u || null)
    setAssignOpen(true)
  }

  const openStatusDialog = (row, defaultToVisited = false) => {
    if (!row?.id) return

    setStatusRow(row)
    setDialogStatus(defaultToVisited ? 'visited' : (row.status || 'pending'))

    const hasReview = row.school_review && row.school_review.replace(/<[^>]*>?/gm, '').trim() !== ''
    setDialogSchoolReview(hasReview ? row.school_review : DEFAULT_SCHOOL_REVIEW_TEMPLATE)
    setDialogCheckedConcepts(
      initialReviewCheckedConcepts(row.concepts_to_explain, row.concepts_explained)
    )

    const pd = row.postponed_date || ''
    setDialogPostponedDate(pd)

    const pt = row.postponed_time

    if (pt && typeof pt === 'string') {
      setDialogPostponedTime(pt.length >= 5 ? pt.slice(0, 5) : pt)
    } else {
      setDialogPostponedTime('')
    }

    setStatusOpen(true)
  }
  const saveAssign = () => {
    if (!assignRow?.id) return
    setAssignSaving(true)
    const payload = visitPayloadFromRow(assignRow, {
      assigned_to: assignUser?.id ?? null,
    })
    putRequest(`${PUT_URL.company_visits.api}${assignRow.id}/`, payload, props)
      .then((res) => {
        if (res && res.status === 200) {
          setAssignOpen(false)
          setAssignRow(null)
          loadRows()
        }
      })
      .finally(() => setAssignSaving(false))
  }

  const saveStatus = () => {
    if (!statusRow?.id) return
    if (dialogStatus === 'postponed') {
      if (!dialogPostponedDate) {
        Swal.fire({ icon: 'warning', title: 'Postponed date is required' })
        return
      }
      if (!dialogPostponedTime) {
        Swal.fire({ icon: 'warning', title: 'Postponed time is required' })
        return
      }
    }
    setStatusSaving(true)
    const showReview = dialogStatus === 'visited'
    const overrides = { status: dialogStatus }
    if (dialogStatus === 'postponed') {
      overrides.postponed_date = dialogPostponedDate
      overrides.postponed_time = dialogPostponedTime.length === 5 ? `${dialogPostponedTime}:00` : dialogPostponedTime
    } else {
      overrides.postponed_date = null
      overrides.postponed_time = null
    }
    if (showReview) {
      overrides.school_review = dialogSchoolReview || ''
      overrides.concepts_explained = normalizeConceptsList(dialogCheckedConcepts)
    }
    const payload = visitPayloadFromRow(statusRow, overrides)
    putRequest(`${PUT_URL.company_visits.api}${statusRow.id}/`, payload, props)
      .then((res) => {
        if (res && res.status === 200) {
          setStatusOpen(false)
          setStatusRow(null)
          loadRows()
        }
      })
      .finally(() => setStatusSaving(false))
  }

  const handleDelete = (row) => {
    if (!row?.id) return
    deleteRequest(`${DEL_URL.company_visits.api}${row.id}/`, {}, props).then((res) => {
      if (res && res.status === 200) loadRows()
    })
  }

  const handleStartVisit = async (visitId) => {
    if (!window.isSecureContext && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
      Swal.fire({
        icon: 'warning',
        title: 'Secure Connection Required',
        text: 'Camera capture and GPS tracking require a secure HTTPS connection.'
      })
      return
    }

    if (!navigator.geolocation) {
      Swal.fire({
        icon: 'error',
        title: 'GPS Not Supported',
        text: 'Your browser or device does not support GPS location tracking.'
      })
      return
    }

    const row = rows.find(r => r.id === visitId)
    capturingVisitRef.current = row
    setCapturedPhoto(null)

    // Trigger location update in background
    navigator.geolocation.getCurrentPosition(
      (position) => {
        locationRef.current = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        }
      },
      (error) => console.error("Error refreshing location:", error),
      { enableHighAccuracy: true, timeout: 5000 }
    )

    // Open the custom camera modal
    setShowCamera(true)
  }

  const handleEndVisit = async (visitId) => {
    let payload = {}
    if (navigator.geolocation) {
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 6000,
            maximumAge: 0
          })
        })
        payload = {
          latitude: position.coords.latitude.toFixed(7),
          longitude: position.coords.longitude.toFixed(7),
          accuracy: position.coords.accuracy
        }
      } catch (err) {
        console.error("GPS capture failed on end_visit:", err)
      }
    }

    postRequest(`${GET_URL.company_visits.api}${visitId}/check_out/`, payload, props).then((res) => {
      if (res && res.status === 200) {
        loadRows()
      } else {
        Swal.fire({ icon: 'error', title: 'Failed to end visit', text: res?.data?.error || '' })
      }
    }).catch(err => {
        Swal.fire({ icon: 'error', title: 'Failed to end visit', text: err?.response?.data?.error || '' })
    })
  }

  const handleCancelCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    setCapturedPhoto(null)
    setShowCamera(false)
  }

  const takePhoto = () => {
    const video = videoRef.current
    if (!video) return

    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth || 640
    canvas.height = video.videoHeight || 480
    const ctx = canvas.getContext('2d')
    
    // Draw the video frame
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

    // Watermark settings
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'
    ctx.fillRect(0, canvas.height - 80, canvas.width, 80)
    
    ctx.font = '16px Arial'
    ctx.fillStyle = '#ffffff'
    
    const visit = capturingVisitRef.current
    const schoolNameStr = visit?.company_name || 'Unknown School'
    
    // Format timestamp
    const now = new Date()
    const timestamp = now.toLocaleString()

    // Format location
    let locationStr = 'Location: Fetching...'
    if (locationRef.current) {
      locationStr = `Lat: ${locationRef.current.latitude.toFixed(5)}, Lng: ${locationRef.current.longitude.toFixed(5)}`
    }

    ctx.fillText(schoolNameStr, 10, canvas.height - 55)
    ctx.fillText(timestamp, 10, canvas.height - 35)
    ctx.fillText(locationStr, 10, canvas.height - 15)

    const dataUrl = canvas.toDataURL('image/jpeg')

    canvas.toBlob((blob) => {
      if (blob) {
        setCapturedPhoto({ dataUrl, blob })
      }
    }, 'image/jpeg', 0.85)
  }

  const discardPhoto = () => {
    setCapturedPhoto(null)
  }

  const saveAndUploadPhoto = async () => {
    const visit = capturingVisitRef.current
    if (!capturedPhoto || !visit) return

    Swal.fire({
      title: 'Uploading...',
      text: 'Uploading captured photo with location...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading()
      }
    })

    const file = new File([capturedPhoto.blob], `visit_photo_${Date.now()}.jpg`, { type: 'image/jpeg' })

    const doStartVisitAndUpload = async (lat, lng) => {
      try {
        // Upload photo
        const formData = new FormData()
        formData.append('company_visit', visit.id)
        formData.append('file', file)
        formData.append('caption', 'Check In Photo')
        formData.append('media_type', 'photo')
        if (lat) formData.append('latitude', parseFloat(lat).toFixed(7))
        if (lng) formData.append('longitude', parseFloat(lng).toFixed(7))

        await api.post('media/api/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })

        // Then call start_visit API
        const payload = {}
        if (lat) payload.latitude = parseFloat(lat).toFixed(7)
        if (lng) payload.longitude = parseFloat(lng).toFixed(7)
        if (locationRef.current?.accuracy) payload.accuracy = locationRef.current.accuracy

        const res = await postRequest(`${GET_URL.company_visits.api}${visit.id}/check_in/`, payload, props)
        if (res && res.status === 200) {
          Swal.fire({ icon: 'success', title: 'Success', text: 'Visit started successfully', timer: 1500, showConfirmButton: false })
          handleCancelCamera()
          loadRows()
        } else {
          Swal.fire({ icon: 'error', title: 'Failed to start visit', text: res?.data?.error || '' })
        }
      } catch (err) {
        console.error("Failed to upload media or start visit:", err)
        const errorMsg = err.response?.data
          ? (typeof err.response.data === 'object' ? JSON.stringify(err.response.data) : err.response.data)
          : err.message
        Swal.fire({
          icon: 'error',
          title: 'Upload Failed',
          text: `Failed to upload media: ${errorMsg}`
        })
      }
    }

    if (locationRef.current && locationRef.current.latitude) {
      await doStartVisitAndUpload(locationRef.current.latitude, locationRef.current.longitude)
    } else if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          await doStartVisitAndUpload(pos.coords.latitude, pos.coords.longitude)
        },
        async (err) => {
          console.warn("Failed to get live location", err)
          await doStartVisitAndUpload(null, null)
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
      )
    } else {
      await doStartVisitAndUpload(null, null)
    }
  }

  React.useEffect(() => {
    let activeStream = null
    let watchId = null

    const startStream = async () => {
      if (showCamera) {
        if (navigator.geolocation) {
          watchId = navigator.geolocation.watchPosition(
            (pos) => {
              locationRef.current = { latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy }
            },
            (err) => console.warn("Live location watch failed:", err),
            { enableHighAccuracy: true, maximumAge: 0, timeout: 5000 }
          )
        }

        try {
          const constraints = {
            video: {
              facingMode: { ideal: 'environment' },
              width: { ideal: 1280 },
              height: { ideal: 720 }
            },
            audio: false
          }
          const stream = await navigator.mediaDevices.getUserMedia(constraints)
          activeStream = stream
          streamRef.current = stream
          if (videoRef.current) {
            videoRef.current.srcObject = stream
          }
        } catch (err) {
          console.error("Failed to acquire camera stream:", err)
          Swal.fire({
            icon: 'error',
            title: 'Camera Error',
            text: 'Could not access camera. Please check permissions.'
          })
          setShowCamera(false)
        }
      }
    }

    startStream()

    return () => {
      if (activeStream) {
        activeStream.getTracks().forEach(track => track.stop())
      }
      if (watchId !== null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchId)
      }
    }
  }, [showCamera])

  const loadRows = () => {
    setLoading(true)
    const params = {
      limit: pageSize,
      pageno: page + 1,
    }
    if (myVisitsOnly) params.mine = '1'
    if (statusFilter) params.status = statusFilter
    if (debouncedSearchTerm.trim()) params.search = debouncedSearchTerm.trim()
    if (startDate) params.start_date = startDate
    if (endDate) params.end_date = endDate
    if (sortModel?.length) {
      const s = sortModel[0]
      params.ordering = `${s.sort === 'desc' ? '-' : ''}${s.field}`
    } else {
      params.ordering = 'visit_date'
    }

    getRequest(GET_URL.company_visits.api, params, props)
      .then((response) => {
        if (response && response.status === 200) {
          const data = response.data?.data || []
          const count =
            response.data?.count ??
            response.data?.total_count ??
            (Array.isArray(data) ? data.length : 0)
          setRows(Array.isArray(data) ? data : [])
          setRowCount(count)
        }
      })
      .finally(() => setLoading(false))
  }

  const loadSummaryStats = React.useCallback(() => {
    const baseParams = {}
    if (myVisitsOnly) baseParams.mine = '1'
    if (debouncedSearchTerm.trim()) baseParams.search = debouncedSearchTerm.trim()
    if (startDate) baseParams.start_date = startDate
    if (endDate) baseParams.end_date = endDate
    const statuses = ['', 'pending', 'visited', 'postponed', 'cancel']
    Promise.all(
      statuses.map((s) =>
        getRequest(GET_URL.company_visits.api, { ...baseParams, limit: 1, pageno: 1, ...(s ? { status: s } : {}) }, props)
      )
    ).then(([all, pending, visited, postponed, cancel]) => {
      setSummaryStats({
        total: all?.data?.count ?? all?.data?.total_count ?? null,
        pending: pending?.data?.count ?? null,
        visited: visited?.data?.count ?? null,
        postponed: postponed?.data?.count ?? null,
        cancel: cancel?.data?.count ?? null,
      })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myVisitsOnly, debouncedSearchTerm, startDate, endDate])

  const columns = (() => {
    const base = [
      { field: 'id', headerName: 'ID', width: 70 },
      { field: 'company_name', headerName: 'Company', flex: 1, minWidth: 160 },
      {
        field: 'visit_date',
        headerName: 'Visit date',
        width: 120,
        valueFormatter: ({ value }) => {
          if (!value) return '-';
          const [y, m, d] = value.split('-');
          return d && m && y ? `${d}-${m}-${y}` : value;
        }
      },
      {
        field: 'visit_time',
        headerName: 'Visit time',
        width: 100,
        valueFormatter: ({ value }) => formatTime(value),
      },
      {
        field: 'checked_in_at',
        headerName: 'Check In Time',
        width: 160,
        valueFormatter: ({ value }) => {
          if (!value) return '-';
          return new Date(value).toLocaleString();
        }
      },
      {
        field: 'checked_out_at',
        headerName: 'Check Out Time',
        width: 160,
        valueFormatter: ({ value }) => {
          if (!value) return '-';
          return new Date(value).toLocaleString();
        }
      },
      { field: 'visit_type', headerName: 'Visit type', width: 150 },
      {
        field: 'priority',
        headerName: 'Priority',
        width: 100,
        renderCell: (params) => {
          const v = params.value;
          let color = 'default';
          if (v === 'high') color = 'error';
          else if (v === 'medium') color = 'warning';
          else if (v === 'low') color = 'success';
          return <Chip label={v || '-'} size="small" variant="outlined" color={color} sx={{ textTransform: 'capitalize' }} />
        }
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        renderCell: (p) => {
          const v = p.value
          const label = STATUS_LABEL[v] || v || '-'
          let color = 'default'
          if (v === 'visited') color = 'success'
          else if (v === 'pending') color = 'warning'
          else if (v === 'postponed') color = 'info'
          else if (v === 'cancel') color = 'default'
          return <Chip label={label} size="small" variant="outlined" color={color} />
        },
      },
      { field: 'assigned_to_name', headerName: 'Assigned to', width: 150 },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1,
        minWidth: 220,
        renderCell: (params) => <ExpandableCell value={params.value || ''} />,
      },
      {
        field: 'concepts_explained',
        headerName: 'Concepts explained',
        flex: 1,
        minWidth: 180,
        sortable: false,
        renderCell: (params) => <ConceptsExplainedCell value={params.value} />,
      },
      {
        field: 'school_review',
        headerName: 'School review',
        flex: 1,
        minWidth: 220,
        renderCell: (params) => <ExpandableCell value={params.value || ''} isHtml />,
      },
      {
        field: 'media_count',
        headerName: 'Media Count',
        width: 110,
        renderCell: (params) => {
          const count = params.row?.media?.length || 0;
          return (
            <Chip 
              label={count} 
              size="small" 
              color={count > 0 ? "success" : "default"} 
              variant="outlined" 
            />
          );
        }
      }
    ]

    base.unshift({
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      minWidth: 260,
      flex: 0.8,
      renderCell: (params) => {
        const row = params?.row
        const isCanceled = row?.status === 'cancel'
        const isVisited = row?.status === 'visited'

        return (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ py: 0.5 }}>
            {!row.checked_in_at && !isCanceled && (
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<PlayArrow />}
                onClick={() => handleStartVisit(row.id)}
                sx={{ textTransform: 'none' }}
              >
                Check In
              </Button>
            )}

            {row.checked_in_at && !row.checked_out_at && !isCanceled && (
              <Button
                size="small"
                variant="contained"
                color="warning"
                startIcon={<CheckCircleOutline />}
                onClick={() => handleEndVisit(row.id)}
                sx={{ textTransform: 'none' }}
                disabled={!row.media || row.media.length === 0}
              >
                Check Out
              </Button>
            )}

            {!isCanceled && canChangeStatus ? (
              <Button
                size="small"
                variant="outlined"
                component={Link}
                to={`/visits?id=${row.id}`}
                sx={{ textTransform: 'none' }}
              >
                Edit
              </Button>
            ) : null}

            {!isCanceled && canChangeStatus ? (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => openStatusDialog(row, true)}
                sx={{ textTransform: 'none' }}
              >
                Add review
              </Button>
            ) : null}

            {canDelete && !isCanceled && !isVisited ? (
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => handleDelete(row)}
                sx={{ textTransform: 'none' }}
              >
                Cancel visit
              </Button>
            ) : null}
          </Stack>
        )
      }
    })

    return base
  })()

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 400)
    return () => clearTimeout(timer)
  }, [searchTerm])

  React.useEffect(() => {
    setPage(0)
  }, [statusFilter, debouncedSearchTerm, myVisitsOnly, startDate, endDate])

  React.useEffect(() => {
    loadRows()
    loadSummaryStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, debouncedSearchTerm, page, pageSize, sortModel, myVisitsOnly, startDate, endDate])

  const handlePageChange = (newPage) => setPage(newPage)
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize)
    setPage(0)
  }

  const canEditReviewInStatusDialog = dialogStatus === 'visited'
  const statusDialogConcepts = normalizeConceptsList(statusRow?.concepts_to_explain)

  const handleDialogStatusChange = (nextStatus) => {
    setDialogStatus(nextStatus)
    if (nextStatus !== 'visited') return
    const hasReview =
      dialogSchoolReview && dialogSchoolReview.replace(/<[^>]*>?/gm, '').trim() !== ''
    const hadSavedReview =
      statusRow?.school_review &&
      statusRow.school_review.replace(/<[^>]*>?/gm, '').trim() !== ''
    if (!hasReview && !hadSavedReview) {
      setDialogSchoolReview(DEFAULT_SCHOOL_REVIEW_TEMPLATE)
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={6}>
          <Typography variant="h5" fontWeight={600}>
            {myVisitsOnly ? 'My visits' : 'Visit management'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {myVisitsOnly
              ? 'Visits assigned to you. Update status and add your school review when the visit is completed.'
              : 'Manage visits, assignments, and schedules.'}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6} textAlign="right">
          {!myVisitsOnly && canAdd ? (
            <Button
              variant="contained"
              component={Link}
              to="/visits"
              startIcon={<AddCircleOutlineOutlined />}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                px: 2,
                py: 1,
                boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
              }}
            >
              Add visit
            </Button>
          ) : null}
        </Grid>
      </Grid>

      {/* Tabs */}
      {!myVisitsOnly && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, mt: 2 }}>
          <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)}>
            <Tab label="Visit List" />
            <Tab label="Visit Summary" />
          </Tabs>
        </Box>
      )}

      {activeTab === 0 ? (
        <>
          {/* Filters */}
          <Grid container spacing={2} sx={{ mt: 1, mb: 1 }}>

            <Grid item xs={12} sm={6} md={4}>
              <DateRange
                label="Visit Date Range"
                startDate={startDate}
                endDate={endDate}
                handleChange={({ start, end }) => {
                  setStartDate(start || '')
                  setEndDate(end || '')
                }}
              />
            </Grid>
          </Grid>

          {/* DataGrid */}
          <Paper elevation={0} sx={{ mt: 3, p: 2, borderRadius: '16px', boxShadow: '0 6px 20px rgba(0,0,0,0.05)' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              pagination
              paginationMode="server"
              sortingMode="server"
              sortModel={sortModel}
              onSortModelChange={(model) => {
                setPage(0);
                setSortModel(model);
              }}
              page={page}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              rowsPerPageOptions={[10, 20, 50]}
              rowCount={rowCount}
              autoHeight
              disableSelectionOnClick
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f8fafc',
                  color: '#475569',
                  fontWeight: 600,
                },
                '& .MuiDataGrid-virtualScroller': {
                  backgroundColor: '#ffffff',
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #f0f0f0',
                  alignItems: 'flex-start',
                  py: 1,
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#fafafa',
                },
              }}
            />
          </Paper>

          <Dialog open={assignOpen} onClose={() => !assignSaving && setAssignOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Assign visit</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {assignRow?.company_name ? `Company: ${assignRow.company_name}` : ''}
              </Typography>
              <Autocomplete
                options={users}
                isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                getOptionLabel={(o) => o?.username || o?.email || String(o?.id || '')}
                value={assignUser}
                onChange={(_, v) => setAssignUser(v)}
                renderInput={(params) => <TextField {...params} label="Assigned to" margin="normal" />}
              />
              <Button sx={{ mt: 1 }} size="small" onClick={() => setAssignUser(null)}>
                Clear assignment
              </Button>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAssignOpen(false)} disabled={assignSaving}>
                Cancel
              </Button>
              <Button variant="contained" onClick={saveAssign} disabled={assignSaving}>
                {assignSaving ? 'Saving…' : 'Save'}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={statusOpen} onClose={() => !statusSaving && setStatusOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>
              {statusRow?.status === 'visited' ? 'Add review' : 'Change status'}
            </DialogTitle>

            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {statusRow?.company_name ? `Company: ${statusRow.company_name}` : ''}
              </Typography>

              {/* REMOVE STATUS CHANGE FOR ALREADY VISITED UNLESS CAN CHANGE */}
              {!(statusRow?.status === 'visited' && !canChange) ? (
                <TextField
                  select
                  fullWidth
                  margin="normal"
                  label="Status"
                  value={dialogStatus}
                  onChange={(e) => handleDialogStatusChange(e.target.value)}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="visited">Visited</MenuItem>
                  <MenuItem value="postponed">Postponed (reschedule)</MenuItem>
                  <MenuItem value="cancel">Canceled</MenuItem>
                </TextField>
              ) : null}

              {dialogStatus === 'postponed' ? (
                <>
                  <TextField
                    fullWidth
                    margin="normal"
                    type="date"
                    label="Postponed to (date)"
                    InputLabelProps={{ shrink: true }}
                    value={dialogPostponedDate}
                    onChange={(e) => setDialogPostponedDate(e.target.value)}
                    required
                  />

                  <TextField
                    fullWidth
                    margin="normal"
                    type="time"
                    label="Postponed to (time)"
                    InputLabelProps={{ shrink: true }}
                    value={dialogPostponedTime}
                    onChange={(e) => setDialogPostponedTime(e.target.value)}
                    required
                  />
                </>
              ) : null}

              {canEditReviewInStatusDialog && statusDialogConcepts.length > 0 ? (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Concepts to be explained — check the concepts covered during the visit
                  </Typography>
                  <Grid container spacing={0.5}>
                    {statusDialogConcepts.map((concept) => (
                      <Grid item xs={12} sm={6} key={concept}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={isConceptChecked(dialogCheckedConcepts, concept)}
                              onChange={() =>
                                setDialogCheckedConcepts((prev) => toggleReviewConcept(prev, concept))
                              }
                            />
                          }
                          label={concept}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ) : null}

              {canEditReviewInStatusDialog ? (
                <Box sx={{ mt: 2, mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Review
                  </Typography>
                  <Box
                    sx={{
                      background: '#fff',
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: '1px solid rgba(0, 0, 0, 0.23)',
                      '& .ql-toolbar': {
                        borderTopLeftRadius: 4,
                        borderTopRightRadius: 4,
                        background: '#f8fafc',
                        border: 'none',
                        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                      },
                      '& .ql-container': {
                        minHeight: 200,
                        fontSize: '15px',
                        borderBottomLeftRadius: 4,
                        borderBottomRightRadius: 4,
                        border: 'none',
                      },
                      '& .ql-editor': {
                        minHeight: 180,
                      },
                    }}
                  >
                    <ReactQuill
                      theme="snow"
                      value={dialogSchoolReview}
                      onChange={(value) => setDialogSchoolReview(value)}
                      placeholder="Write your review here..."
                    />
                  </Box>
                </Box>
              ) : null}

              {canEditReviewInStatusDialog && statusRow?.company ? (
                <Box sx={{ mt: 2, mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Assigned Checklists
                  </Typography>
                  <CompanyChecklistTab company_id={statusRow.company} hideAssign={true} />
                </Box>
              ) : null}
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setStatusOpen(false)} disabled={statusSaving}>
                Cancel
              </Button>

              <Button variant="contained" onClick={saveStatus} disabled={statusSaving}>
                {statusSaving ? 'Saving…' : 'Save'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : activeTab === 1 ? (
        <VisitSummaryTab mine={myVisitsOnly ? '1' : ''} />
      ) : null}

      {showCamera && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ position: 'relative', width: '100%', maxWidth: '440px', display: 'flex', flexDirection: 'column', background: '#1e293b', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)' }}>
            {capturedPhoto ? (
              <div style={{ display: 'flex', flexDirection: 'column', background: '#1e293b', color: '#fff', padding: '20px' }}>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
                  <img
                    src={capturedPhoto.dataUrl}
                    alt="Captured Preview"
                    style={{ width: '100px', height: '75px', objectFit: 'cover', borderRadius: '6px', border: '1.5px solid #fff' }}
                  />
                  <div>
                    <h3 style={{ margin: 0, fontSize: '15px', color: '#fff' }}>Photo Captured</h3>
                    <p style={{ margin: '3px 0 0 0', fontSize: '13px', color: '#94a3b8' }}>
                      🏫 {capturingVisitRef.current?.company_name || 'Unknown'}
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                  <button
                    type="button"
                    onClick={discardPhoto}
                    style={{ flex: 1, padding: '10px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, border: 'none', cursor: 'pointer', background: '#ef4444', color: 'white' }}
                  >
                    <Trash2 size={14} /> Discard & Retake
                  </button>
                  <button
                    type="button"
                    onClick={saveAndUploadPhoto}
                    style={{ flex: 1, padding: '10px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, border: 'none', cursor: 'pointer', background: '#10b981', color: 'white' }}
                  >
                    <CheckCircle size={14} /> Upload & Check In
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ position: 'relative', width: '100%', aspectRatio: '4/3', background: '#000', overflow: 'hidden' }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />

                {/* Top Close Button */}
                <button
                  onClick={handleCancelCamera}
                  style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'rgba(0,0,0,0.5)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '36px',
                    height: '36px',
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10
                  }}
                  title="Close Camera"
                >
                  <X size={20} />
                </button>

                {/* Bottom Controls */}
                <div style={{
                  position: 'absolute',
                  bottom: '20px',
                  left: 0,
                  right: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 10
                }}>
                  <button
                    onClick={takePhoto}
                    style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '50%',
                      background: '#fff',
                      border: '5px solid rgba(255,255,255,0.3)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                      transition: 'transform 0.1s ease'
                    }}
                    title="Capture Photo"
                  >
                    <div style={{ width: '46px', height: '46px', borderRadius: '50%', background: '#fff', border: '1.5px solid #000' }} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Box>
  )
}

export default VisitList;
